// components/views/sections/DomainBarChart.js
import { Brain, User } from 'lucide-react';
import { getDomainInfo, getReactIcon } from '../../hooks_suggestion/useDomainSuggestions';

export const DomainBarChart = ({ domainKPIs, hasAnyDomain }) => {
    // Si aucun domaine, afficher un message informatif
    if (!hasAnyDomain) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                    <Brain className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">Aucun domaine d'expertise</p>
                    <p className="text-sm mb-4">
                        Créez vos premiers domaines pour visualiser votre progression
                    </p>
                    <div className="text-xs text-gray-400">
                        Le graphique en barres vous permettra de comparer vos domaines facilement
                    </div>
                </div>
            </div>
        );
    }

    // Trier les domaines par score décroissant
    const sortedDomains = [...domainKPIs].sort((a, b) => b.overallScore - a.overallScore);

    // Fonction pour obtenir la couleur basée sur le score (même palette que heatmap)
    const getColor = (score) => {
        if (score === 0) return '#e5e7eb'; // Gris pour non évalué
        if (score > 60) return '#0e9488'; // Vert teal pour 50%+
        if (score > 40) return '#ffad65'; // Orange pour 35-49%
        return '#e57a86'; // Rouge/rose pour 1-34%
    };

    return (
        <div className="h-full flex flex-col">
            {/* Graphique en barres */}
            <div className="flex-1 flex flex-col gap-2 overflow-y-auto">
                {sortedDomains.map((domain, index) => {
                    // Récupérer les informations du domaine et l'icône (même logique que DomainSection)
                    const domainInfo = getDomainInfo(domain.name || domain.id);
                    const displayName = domainInfo?.nom || domain.name;
                    const displayIcon = domainInfo?.icon || domain.icon;
                    
                    // Récupérer l'icône React
                    const IconComponent = displayIcon ? getReactIcon(displayIcon) : null;
                    
                    return (
                        <div key={domain.id} className="flex items-center gap-3 min-h-[52px] group">
                            {/* Section gauche: Icône + trait + nom */}
                            <div className="flex items-center gap-3 w-60 flex-shrink-0">
                                {/* Icône */}
                                <div className="flex items-center justify-center w-8 h-8 flex-shrink-0">
                                    {IconComponent ? (
                                        <IconComponent className="w-4 h-4 text-gray-500" />
                                    ) : displayIcon ? (
                                        <span className="text-lg">
                                            {displayIcon}
                                        </span>
                                    ) : (
                                        <User className="w-3 h-3 text-gray-400" />
                                    )}
                                </div>

                                {/* Trait vertical */}
                                <div className="w-px h-8 bg-gray-300 flex-shrink-0"></div>

                                {/* Nom du domaine */}
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm text-gray-900 leading-tight mb-0.5 truncate" title={displayName}>
                                        {displayName}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {domain.skillsWithRating}/{domain.totalSkills} éval.
                                    </div>
                                </div>
                            </div>

                            {/* Trait vertical séparateur principal */}
                            <div className="w-px h-12 bg-gray-300 flex-shrink-0"></div>

                            {/* Section droite: Barres de progression */}
                            <div className="flex-1 flex items-center gap-3">
                                <div className="flex-1 bg-gray-200 rounded-md h-6 relative overflow-hidden">
                                    <div
                                        className="h-full transition-all duration-300 ease-out rounded-md"
                                        style={{
                                            width: `${Math.max(domain.overallScore, 5)}%`,
                                            backgroundColor: getColor(domain.overallScore)
                                        }}
                                    >
                                    </div>
                                    
                                    {/* Pourcentage overlay */}
                                    <div className="absolute inset-0 flex items-center justify-start pl-3">
                                        <span className={`text-xs font-semibold ${
                                            domain.overallScore === 0 ? 'text-gray-500' : 
                                            domain.overallScore >= 50 ? 'text-white' : 'text-gray-700'
                                        }`}>
                                            {domain.overallScore === 0 ? 'Non évalué' : `${domain.overallScore}%`}
                                        </span>
                                    </div>
                                </div>

                                {/* Score détaillé */}
                                <div className="flex items-center gap-1 text-xs text-gray-600 w-16 flex-shrink-0 font-medium">
                                    <span className="text-gray-400">●</span>
                                    <span>{domain.totalRating}/{domain.maxPossible}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Légende */}
            <div className="mt-4 pt-3 border-t border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Échelle de maîtrise :</span>
                    <div className="flex items-center gap-1">
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#e5e7eb' }}></div>
                            <span className="text-gray-500">0%</span>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#e57a86' }}></div>
                            <span className="text-gray-500">1-34%</span>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#ffad65' }}></div>
                            <span className="text-gray-500">35-49%</span>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#0e9488' }}></div>
                            <span className="text-gray-500">≥50%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};